import { Box, Link } from '@chakra-ui/layout'
import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FiEdit } from 'react-icons/fi'
import { RiDeleteBinFill } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import PermissionGuard from '../../components/PermissionGuard'
import {
  NamedTransformsDeleteModal,
  NamedTransformsEditModal,
} from '../../components/Settings'
import type { SettingsResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'

const NamedTransformsSettingsLayout = ({
  form,
  getData,
}: {
  form: FormikProps<SettingsResponse>
  getData(): Promise<void>
}): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  const { values } = form

  const {
    isOpen: isOpenEditForm,
    onClose: onCloseEditForm,
    onOpen: onOpenEditForm,
  } = useDisclosure({
    onClose: () => {
      setCurNamedTransform(null)
    },
  })

  const {
    isOpen: isOpenDeleteForm,
    onClose: onCloseDeleteForm,
    onOpen: onOpenDeleteForm,
  } = useDisclosure({
    onClose: () => {
      setCurNamedTransform(null)
    },
  })

  const [curNamedTransform, setCurNamedTransform] = useState<string | null>(
    null,
  )

  return (
    <>
      <MainContentHeadingBox />
      <MainContentWrapper>
        <ChakraText color={colors.appBattleshipGrey}>
          <Trans t={t} i18nKey="named_transform_description">
            Named Transformations are aliases for actual image transformations
            which allow for easier maintenance and collaboration in large teams.
            For example, you can alias{' '}
            <b>
              tr:w-150,h-150,fo-center,cm-resize as tr:n-media_library_thumbnail
            </b>{' '}
            and use this in your image URLs.{' '}
          </Trans>
          <Link
            href="https://docs.imagekit.io/features/named-transformations"
            target="_blank"
          >
            {t('read_more')}
          </Link>
        </ChakraText>
        <Box marginTop="2em" mb="0.5em">
          <ChakraText fontWeight="700" marginBottom="10px">
            {t('named_transformation')}
          </ChakraText>
          <PermissionGuard
            permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission ? (
                <Button onClick={onOpenEditForm}>+ Add New</Button>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Box>
        <Table marginTop="16px">
          <Thead background="appPaleGreyTwo">
            <Tr>
              <Th textTransform="capitalize" letterSpacing="none">
                {t('named_transform')}
              </Th>
              <Th textTransform="capitalize" letterSpacing="none">
                {t('actual_tranform_string')}
              </Th>
              <Th textTransform="capitalize" letterSpacing="none">
                {t('status')}
              </Th>
              <PermissionGuard
                permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
              >
                {(hasPermission: boolean) =>
                  hasPermission ? (
                    <Th textTransform="capitalize" letterSpacing="none">
                      &nbsp;
                    </Th>
                  ) : (
                    <></>
                  )
                }
              </PermissionGuard>
            </Tr>
          </Thead>
          <Tbody>
            {Object.entries(values.namedTransform).map(([id, transform]) =>
              transform.isDefault ? (
                <Tr key={id}>
                  <Td maxW="10em">
                    <Tooltip label={transform.label}>
                      <ChakraText isTruncated>{transform.label}</ChakraText>
                    </Tooltip>
                  </Td>
                  <Td maxW="10em">
                    <Tooltip label={transform.value}>
                      <ChakraText isTruncated>{transform.value}</ChakraText>
                    </Tooltip>
                  </Td>
                  <Td>&nbsp;</Td>
                  <PermissionGuard
                    permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
                  >
                    {(hasPermission: boolean) =>
                      hasPermission ? <Td>&nbsp;</Td> : <></>
                    }
                  </PermissionGuard>
                </Tr>
              ) : (
                <Tr key={id}>
                  <Td maxW="10em">
                    <Tooltip label={transform.label}>
                      <ChakraText isTruncated>{transform.label}</ChakraText>
                    </Tooltip>
                  </Td>
                  <Td maxW="10em">
                    <Tooltip label={transform.value}>
                      <ChakraText isTruncated>{transform.value}</ChakraText>
                    </Tooltip>
                  </Td>
                  {transform.disabled ? (
                    <Td color={colors.appWatermelon}>{t('disabled')}</Td>
                  ) : (
                    <Td color={colors.appGreenishTeal}>{t('enabled')}</Td>
                  )}

                  <PermissionGuard
                    permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
                  >
                    {(hasPermission: boolean) =>
                      hasPermission ? (
                        <Td maxW="1em">
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label="Options"
                              icon={<BsThreeDotsVertical />}
                              variant="link"
                              boxShadow="none"
                            />
                            <MenuList minWidth="10em">
                              <MenuItem
                                icon={<FiEdit />}
                                onClick={() => {
                                  setCurNamedTransform(id)
                                  onOpenEditForm()
                                }}
                              >
                                Edit
                              </MenuItem>
                              <MenuDivider />
                              <MenuItem
                                icon={<RiDeleteBinFill />}
                                color="appBrick"
                                onClick={() => {
                                  setCurNamedTransform(id)
                                  onOpenDeleteForm()
                                }}
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      ) : (
                        <></>
                      )
                    }
                  </PermissionGuard>
                </Tr>
              ),
            )}
          </Tbody>
        </Table>
      </MainContentWrapper>
      <NamedTransformsEditModal
        currentNamedTransform={curNamedTransform}
        isOpen={isOpenEditForm}
        onClose={onCloseEditForm}
        user={user}
        values={values}
        request={request}
        getData={getData}
      />
      {curNamedTransform ? (
        <NamedTransformsDeleteModal
          currentNamedTransform={curNamedTransform}
          isOpen={isOpenDeleteForm}
          onClose={onCloseDeleteForm}
          user={user}
          values={values}
          request={request}
          getData={getData}
        />
      ) : null}
    </>
  )
}

export default NamedTransformsSettingsLayout
